import React from "react";
import PropTypes from "prop-types";
import {
  computePercentDistance,
  computeReaisDistance,
  computeTicksBetween,
  formatPrice,
  normalizePrice,
} from "../../utils/priceUtils";
import { formatNumber, formatVolume } from "../../utils/numberFormatting";

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const getBookPriceKey = (symbol, price) => {
  const n = asNumber(price);
  if (n === null) return null;
  const normalized = normalizePrice(symbol, n);
  return Number.isFinite(normalized) ? String(normalized) : null;
};

const getEffectiveOrderPrice = (order) =>
  asNumber(order?.price ?? order?.limit ?? order?.stop);

const normalizeSide = (side) => {
  const s = String(side || "").toLowerCase();
  if (s === "buy" || s === "sell") return s;
  return "neutral";
};

const addAnnotation = (target, symbol, price, annotation) => {
  const key = getBookPriceKey(symbol, price);
  if (!key) return;
  if (!target[key]) target[key] = [];
  target[key].push(annotation);
};

const buildOrderLabel = (side, quantity) => {
  const sideLabel = side === "buy" ? "C" : side === "sell" ? "V" : "O";
  return `${sideLabel} ${formatVolume(quantity || 0)}`;
};

const buildOtoInfo = ({ symbol, mainPrice, otoPrice, quantity }) => {
  const parts = [];
  const ticks = computeTicksBetween(symbol, mainPrice, otoPrice);
  if (ticks !== undefined) parts.push(`${Math.abs(ticks)} tks`);

  const reais = computeReaisDistance(symbol, mainPrice, otoPrice, quantity);
  if (reais !== undefined) parts.push(`R$ ${formatNumber(Math.abs(reais), 2)}`);

  const percent = computePercentDistance(mainPrice, otoPrice);
  if (percent !== undefined) parts.push(`${Math.abs(percent).toFixed(1)}%`);

  return {
    compact: ticks !== undefined ? `${Math.abs(ticks)}t` : "",
    full: parts.join(" · "),
  };
};

export const buildBookAnnotationsByPrice = ({
  symbol,
  orders = [],
  position,
  priceOverrides,
}) => {
  const byPrice = {};

  orders.forEach((order) => {
    const side = normalizeSide(order.side);
    // priceOverrides feeds live drag-preview prices without touching the store.
    const override = priceOverrides?.[order.id];
    const price = override?.main ?? getEffectiveOrderPrice(order);
    addAnnotation(byPrice, symbol, price, {
      id: `order:${order.id}`,
      kind: "order",
      orderId: order.id,
      price,
      side,
      state: order.state,
      quantity: Number(order.quantity) || 0,
      label: buildOrderLabel(side, order.quantity),
      title: `${side === "buy" ? "Compra" : "Venda"} ${formatVolume(order.quantity || 0)} @ ${formatPrice(symbol, price)}`,
    });

    (order.oto || []).forEach((oto) => {
      const otoPrice = override?.oto?.[oto.id] ?? getEffectiveOrderPrice(oto);
      const kind = oto.type === "profit" ? "profit" : "loss";
      const info = buildOtoInfo({
        symbol,
        mainPrice: price,
        otoPrice,
        quantity: oto.quantity || order.quantity,
      });
      const kindLabel = kind === "profit" ? "GANHO" : "PERDA";
      const compactKindLabel = kind === "profit" ? "G" : "P";
      const quantityLabel = formatVolume(oto.quantity || order.quantity || 0);
      addAnnotation(byPrice, symbol, otoPrice, {
        id: `oto:${order.id}:${oto.id}`,
        kind,
        orderId: order.id,
        otoOrderId: oto.id,
        price: otoPrice,
        side: normalizeSide(oto.side),
        state: oto.state || order.state || "local",
        quantity: Number(oto.quantity || order.quantity) || 0,
        label: `${compactKindLabel} ${quantityLabel}`,
        detail: info.full,
        title: `${kindLabel} @ ${formatPrice(symbol, otoPrice)}${info.full ? ` · ${info.full}` : ""}`,
      });
    });
  });

  if (position?.quantity && position.price) {
    const quantity = Number(position.quantity);
    addAnnotation(byPrice, symbol, position.price, {
      id: `position:${position.symbol || symbol}`,
      kind: "position",
      side: quantity >= 0 ? "buy" : "sell",
      label: `POS ${quantity > 0 ? "+" : ""}${formatVolume(quantity)}`,
      title: `Posição ${formatVolume(quantity)} @ ${formatPrice(symbol, position.price)}`,
    });
  }

  return byPrice;
};

export const getBookAnnotationsForPrice = (
  annotationsByPrice,
  symbol,
  price,
) => {
  const key = getBookPriceKey(symbol, price);
  return key ? annotationsByPrice?.[key] || [] : [];
};

export const FRAMED_KINDS = new Set(["order", "loss", "profit"]);

const SIDE_LETTER = { buy: "C", sell: "V", neutral: "O" };

// Order/leg side (buy/sell/neutral) — drives the frame colour. Legs take
// their own side too, so the book matches the chart's order colours.
const annotationTone = (annotation) => annotation.side;

export const getFramableAnnotations = (annotations = []) =>
  annotations.filter((a) => FRAMED_KINDS.has(a.kind) && a.orderId);

export const BookPriceCell = ({
  symbol,
  price,
  annotations,
  align = "right",
  onAnnotationPointerDown,
  onStackPointerDown,
  onAnnotationCancel,
  onStackOpen,
  draggingOrderIds,
}) => {
  // order / loss / profit render as a frame enclosing the whole price cell
  // ("moldura"). 2+ at the same price collapse into one aggregate frame that
  // opens a panel on click. position stays a small pill beside the price.
  const framable = getFramableAnnotations(annotations);

  if (framable.length > 1) {
    const tones = new Set(framable.map(annotationTone));
    const stackTone = tones.size === 1 ? [...tones][0] : "mixed";
    const stackDragging =
      !!draggingOrderIds &&
      framable.some((a) => draggingOrderIds.includes(a.orderId));

    // Quantity summary grouped by side — C (buy) / V (sell), one segment per
    // side present, each showing the summed quantity at this price.
    const qtyBySide = framable.reduce((acc, a) => {
      acc[a.side] = (acc[a.side] || 0) + (a.quantity || 0);
      return acc;
    }, {});
    const sideSummary = ["buy", "sell", "neutral"]
      .filter((s) => qtyBySide[s] > 0)
      .map((s) => ({
        side: s,
        text: `${SIDE_LETTER[s]} ${formatVolume(qtyBySide[s])}`,
      }));

    const stackClass = [
      "bbp-price-cell",
      `bbp-price-cell--${align}`,
      "bbp-price-cell--framed",
      "bbp-price-cell--stack",
      `bbp-price-cell--framed-${stackTone}`,
      stackDragging && "bbp-price-cell--framed-dragging",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <span
        className={stackClass}
        title={`${framable.length} ordens neste preço`}
        data-evo-id="bookbypricepanel-superdom-order-stack"
        onPointerDown={(event) => onStackPointerDown?.(event, price, framable)}
        onClick={(event) => onStackOpen?.(event, price)}
      >
        <span className="bbp-price-cell__stack-count">{framable.length}</span>
        <span className="bbp-price-cell__stack-summary">
          {sideSummary.map((seg) => (
            <span
              key={seg.side}
              className={`bbp-price-cell__stack-side bbp-price-cell__stack-side--${seg.side}`}
            >
              {seg.text}
            </span>
          ))}
        </span>
        <span className="bbp-price-cell__value">
          {formatPrice(symbol, price)}
        </span>
        <span className="bbp-price-cell__stack-caret" aria-hidden="true">
          ▾
        </span>
      </span>
    );
  }

  const framed = framable[0] || null;
  const pillAnnotations = framed
    ? annotations.filter((a) => a !== framed)
    : annotations;
  const framedDragging =
    !!framed && !!draggingOrderIds && draggingOrderIds.includes(framed.orderId);
  const cancellable = framed?.kind === "order";
  const tone = framed && annotationTone(framed);

  const cellClass = [
    "bbp-price-cell",
    `bbp-price-cell--${align}`,
    framed && "bbp-price-cell--framed",
    framed && `bbp-price-cell--framed-${tone}`,
    framed && `bbp-price-cell--framed-state-${framed.state || "open"}`,
    framedDragging && "bbp-price-cell--framed-dragging",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={cellClass}
      title={framed ? framed.title : undefined}
      onPointerDown={
        framed ? (event) => onAnnotationPointerDown?.(event, framed) : undefined
      }
    >
      {framed && (
        <span className="bbp-price-cell__framed-label">{framed.label}</span>
      )}
      <span className="bbp-price-cell__value">
        {formatPrice(symbol, price)}
      </span>
      {cancellable && (
        <button
          type="button"
          className="bbp-price-marker__cancel"
          title="Cancelar ordem"
          aria-label="Cancelar ordem"
          data-evo-id="bookbypricepanel-superdom-order-cancel"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onAnnotationCancel?.(framed);
          }}
        >
          ×
        </button>
      )}
      {pillAnnotations.length > 0 && (
        <span
          className="bbp-price-cell__markers"
          aria-label="Marcações de ordens e posição"
        >
          {pillAnnotations.map((annotation) => {
            const draggable =
              annotation.kind === "loss" || annotation.kind === "profit";
            const dragging =
              draggable &&
              !!draggingOrderIds &&
              draggingOrderIds.includes(annotation.orderId);
            return (
              <span
                key={annotation.id}
                className={`bbp-price-marker bbp-price-marker--${annotation.kind} bbp-price-marker--${annotation.side} bbp-price-marker--state-${annotation.state || "open"} ${draggable ? "bbp-price-marker--draggable" : ""} ${dragging ? "bbp-price-marker--dragging" : ""}`}
                title={annotation.title}
                onPointerDown={
                  draggable
                    ? (event) => onAnnotationPointerDown?.(event, annotation)
                    : undefined
                }
              >
                {annotation.label}
              </span>
            );
          })}
        </span>
      )}
    </span>
  );
};

BookPriceCell.propTypes = {
  symbol: PropTypes.string,
  price: PropTypes.number,
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(["order", "position", "loss", "profit"]).isRequired,
      orderId: PropTypes.string,
      otoOrderId: PropTypes.string,
      price: PropTypes.number,
      side: PropTypes.oneOf(["buy", "sell", "neutral"]).isRequired,
      state: PropTypes.string,
      label: PropTypes.string.isRequired,
      detail: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  align: PropTypes.oneOf(["left", "center", "right"]),
  onAnnotationPointerDown: PropTypes.func,
  onStackPointerDown: PropTypes.func,
  onAnnotationCancel: PropTypes.func,
  onStackOpen: PropTypes.func,
  draggingOrderIds: PropTypes.arrayOf(PropTypes.string),
};

BookPriceCell.defaultProps = {
  annotations: [],
};
